import { useCallback, useEffect, useRef, useState } from "react";
import { api, getToken } from "../utils/helpers";

export const DEFAULT_BRANDING = {
  title: "CPI Monitor", subtitle: "SAP Integration Suite Dashboard", logoUrl: "", faviconUrl: "",
};

// Branding belongs to the current account, never to the browser's last account.
export default function useBranding(user) {
  const scope = JSON.stringify([user?.id || null, user?.role, user?.customerIds ?? user?.customerId]);
  const authenticated = !!user?.id;
  const accountId = user?.id || null;
  const session = useRef({ accountId, token: getToken() });
  if (session.current.accountId !== accountId) session.current = { accountId, token: getToken() };
  const accountToken = session.current.token;
  const [revision, setRevision] = useState(0);
  const [snapshot, setSnapshot] = useState(null);
  const reload = useCallback(() => setRevision(value => value + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    const token = getToken();
    let active = true;
    // Another tab may replace localStorage before this tab receives its storage
    // event. Never fetch another account's branding under the old user context.
    if (authenticated && token !== accountToken) {
      setSnapshot(null);
      return () => controller.abort();
    }
    setSnapshot(previous => previous?.scope === scope ? { ...previous, error: null } : null);
    const request = authenticated
      ? api("/api/branding/effective", { signal: controller.signal })
      : fetch("/api/branding", { signal: controller.signal, cache: "no-store" }).then(response => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      });
    request.then(data => {
      if (!active || getToken() !== token) return;
      if (!data || typeof data.title !== "string") throw new Error("INVALID_BRANDING_RESPONSE");
      setSnapshot({ scope, data: { ...DEFAULT_BRANDING, ...data }, error: null });
    }).catch(error => {
      if (!active || getToken() !== token || error.name === "AbortError") return;
      setSnapshot(previous => ({ scope, data: previous?.scope === scope ? previous.data : null, error }));
    });
    return () => { active = false; controller.abort(); };
  }, [scope, authenticated, accountToken, revision]);

  useEffect(() => {
    if (!authenticated) return undefined;
    // Pick up assignments and branding changed by another administrator.
    window.addEventListener("focus", reload);
    return () => window.removeEventListener("focus", reload);
  }, [authenticated, reload]);

  const current = snapshot?.scope === scope && (!authenticated || getToken() === accountToken) ? snapshot : null;
  return { branding: current?.data || DEFAULT_BRANDING, brandingError: current?.error, reloadBranding: reload };
}
